import { FaroAiChip } from "@/components/common/ai-assets/ai-chip";
import {
  CircularProgress,
  CompassGoToIcon,
  cyan,
  FaroChipList,
  FaroChipStatus,
  FaroChipTag,
  FaroIconButton,
  FaroMenuSeparator,
  FaroTooltip,
  NO_TRANSLATE_CLASS,
  NoTranslate,
  TruncatedFaroText,
} from "@faro-lotv/flat-ui";
import { Grid } from "@mui/material";
import { Stack } from "@mui/system";
import type { JSX } from "react";
import { CardAttachmentIcon } from "./card-attachment-icon";
import { CardContextMenu } from "./card-context-menu";
import { CardUserAvatar } from "./card-user-avatar";
import { AnnotationCardProps } from "./responsive-card-types";
import {
  RESPONSIVE_CARDS_HEIGHT,
  ResponsiveCardVariant,
} from "./responsive-card-variant";

/** @returns A card with a layout on two different lines */
export function MediumCard({
  name,
  attachments,
  tags,
  state,
  mainAssignee,
  markup,
  aiMarkupData,
  dueDate,
  onGoTo,
  isLoading = false,
}: AnnotationCardProps): JSX.Element {
  return (
    <Stack
      sx={{ height: RESPONSIVE_CARDS_HEIGHT[ResponsiveCardVariant.medium] }}
    >
      <Stack sx={{ p: 1.5, gap: 1 }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            gap: 0.75,
          }}
        >
          {isLoading && (
            <CircularProgress
              sx={{
                color: cyan[500],
              }}
              size="14px"
            />
          )}
          <TruncatedFaroText
            variant="heading12"
            className={NO_TRANSLATE_CLASS}
            containerSx={{ flexGrow: 1 }}
          >
            {name}
          </TruncatedFaroText>
          <CardAttachmentIcon count={attachments.length} />
          <FaroTooltip title="Go to annotation">
            <FaroIconButton
              size="s"
              aria-label="go to annotation"
              onClick={onGoTo}
              margin={0}
            >
              <CompassGoToIcon />
            </FaroIconButton>
          </FaroTooltip>
          <CardContextMenu
            markup={markup}
            mainAssignee={mainAssignee}
            state={state}
            dueDate={dueDate}
          />
        </Stack>
        <Grid
          container
          rowSpacing={0.5}
          columnSpacing={1}
          sx={{ alignItems: "leading" }}
        >
          <Grid
            size="auto"
            sx={{ minWidth: aiMarkupData?.aiDetectedTag ? "0px" : "100px" }}
          >
            {aiMarkupData?.aiDetectedTag ? (
              <FaroAiChip
                label={aiMarkupData.aiDetectedTag}
                size="small"
                textTransform="uppercase"
              />
            ) : (
              <FaroChipStatus status={state} />
            )}
          </Grid>
          <Grid size="grow" sx={{ minWidth: 0 }}>
            <Tags tags={tags} />
          </Grid>
          <Grid size="auto">
            <CardUserAvatar
              userDisplayInfo={mainAssignee}
              isTeam={mainAssignee?.authorityType === "UserGroupRef"}
            />
          </Grid>
        </Grid>
      </Stack>
      <FaroMenuSeparator />
    </Stack>
  );
}

/** @returns a single tag, or a list of tags that will collapse if needed */
function Tags({ tags }: Pick<AnnotationCardProps, "tags">): JSX.Element | null {
  if (!tags) return null;
  return (
    <FaroChipList
      size="small"
      sx={{ flexGrow: 1 }}
      chips={tags.map((t) => (
        <FaroChipTag
          tooltip={<NoTranslate>{t.label}</NoTranslate>}
          className={NO_TRANSLATE_CLASS}
          key={t.id}
          {...t}
        />
      ))}
    />
  );
}
